import React, { useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { HumanoidBack, HumanoidFront } from '../../art/licensed/Humanoid'
import { L1HumanoidMarker, MarkerProps } from './L1HumanDiagramMarker'
import { LeftFoot, LeftHand } from '../../art/commissioned/Appendage'
import { FusionContentZIndex } from '../../../theme/FusionStyle'

export const HUMAN_W = 208

export const L1HumanoidRoot = styled.div({
	position: 'relative',
	width: HUMAN_W,
})

export const L1HumanoidFrontVector = styled(HumanoidFront)({
	zIndex: FusionContentZIndex.Content,
	width: '100%',
	height: 'auto',
})

export const L1HumanoidBackVector = styled(HumanoidBack)({
	zIndex: FusionContentZIndex.Content,
	width: '100%',
	height: 'auto',
})

type L1HumanoidLabelProps = {
	left: number,
	top: number,
}

export const L1HumanoidLabel = styled.div<L1HumanoidLabelProps>(({theme, left, top}) => ({
	...theme.fonts.titleBold,
	position: 'absolute',
	left,
	top,
	textTransform: 'uppercase',
}))

export enum HumanDiagram {
	Humanoid,
	HumanoidBack,
	Hand,
	Foot,
}

export const L1AmputationHumanoidDemo = () => {
	const [markers, setMarkers] = useState<MarkerProps[]>([
		{ cx: 48, cy: 130, active: true, },
		{ cx: 36, cy: 188, active: true, },
		{ cx: 80, cy: 296, active: true, },
		{ cx: 82, cy: 368, active: true, },
		{ cx: 80, cy: 432, active: true, },
		{ cx: (HUMAN_W-48), cy: 130, active: true, },
		{ cx: (HUMAN_W-36), cy: 188, active: true, },
		{ cx: (HUMAN_W-80), cy: 296, active: true, },
		{ cx: (HUMAN_W-82), cy: 368, active: true, },
		{ cx: (HUMAN_W-80), cy: 432, active: true, },
	])
	const handleHumanoidClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
		console.debug(e.nativeEvent.offsetX, e.nativeEvent.offsetY)
		setMarkers([
			...markers,
			{
				cx: e.nativeEvent.offsetX,
				cy: e.nativeEvent.offsetY,
				active: true,
			}
		])
	}, [markers, setMarkers])
	return (
		<L1HumanoidRoot>
			<L1HumanoidFrontVector onClick={handleHumanoidClick} />
			{markers.map((marker, idx) => (
				<L1HumanoidMarker key={idx} {...marker} />
			))}
		</L1HumanoidRoot>
	)
}

export enum Side {
	Left = 'left',
	Right = 'right',
}

interface SideProps {
	side: Side
}

export const HAND_W = 104

export const L1HumanHandRoot = styled.div({
	position: 'relative',
	width: HAND_W,
	// cut off extra bottom illustration
	height: '204px',
	overflow: 'hidden',
})

export const L1HumanHandVector = styled(LeftHand)(({side}: SideProps) => ({
	zIndex: FusionContentZIndex.Content,
	width: '100%',
	height: 'auto',
	transform: side == Side.Right ? 'scale(-1,1)' : 'none',
}))

export const L1AmputationHumanHandDemo = ({side}: SideProps) => {
	const [markers, setMarkers] = useState<MarkerProps[]>([])
	const handleHumanoidClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
		console.debug(e.nativeEvent.offsetX, e.nativeEvent.offsetY)
		setMarkers([
			...markers,
			{
				cx: e.nativeEvent.offsetX,
				cy: e.nativeEvent.offsetY,
				active: true,
			}
		])
	}, [markers, setMarkers])
	const flip = side == Side.Right
	useEffect(() => {
		setMarkers([
			{ cx: flip ? HAND_W - 42 : 42, cy: 92, active: true, },
			{ cx: flip ? HAND_W - 82 : 82, cy: 128, rotateDeg: flip ? -30 : 30, active: true, },
		])
	}, [flip])
	return (
		<L1HumanHandRoot>
			<L1HumanHandVector side={side} onClick={handleHumanoidClick} />
			{markers.map((marker, idx) => (
				<L1HumanoidMarker key={idx} {...marker} />
			))}
		</L1HumanHandRoot>
	)
}

export const FOOT_W = 104

export const L1HumanFootRoot = styled.div({
	position: 'relative',
	width: FOOT_W,
	// cut off extra bottom illustration
	height: '230px',
	overflow: 'hidden',
})

export const L1HumanFootVector = styled(LeftFoot)(({side}: SideProps) => ({
	zIndex: FusionContentZIndex.Content,
	width: '100%',
	height: 'auto',
	transform: side == Side.Right ? 'scale(-1,1)' : 'none',
}))

export const L1AmputationHumanFootDemo = ({side}: SideProps) => {
	const [markers, setMarkers] = useState<MarkerProps[]>([])
	const handleHumanoidClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
		console.debug(e.nativeEvent.offsetX, e.nativeEvent.offsetY)
		setMarkers([
			...markers,
			{
				cx: e.nativeEvent.offsetX,
				cy: e.nativeEvent.offsetY,
				active: true,
			}
		])
	}, [markers, setMarkers])
	const flip = side == Side.Right
	useEffect(() => {
		setMarkers([
			{ cx: flip ? FOOT_W - 88 : 88, cy: 62, active: true, },
			{ cx: flip ? FOOT_W - 36 : 36, cy: 74, rotateDeg: flip ? 25 : -25, active: true, },
			{ cx: flip ? FOOT_W - 42 : 42, cy: 162, active: true, },
		])
	}, [flip])
	return (
		<L1HumanFootRoot>
			<L1HumanFootVector side={side} onClick={handleHumanoidClick} />
			{markers.map((marker, idx) => (
				<L1HumanoidMarker key={idx} {...marker} />
			))}
		</L1HumanFootRoot>
	)
}
